import type { NextRouter } from "next/router";
import {
  appendQueryInterpretationToParams,
  normalizeZipcode,
  type SearchLocationSource,
  type SearchQueryInterpretation,
} from "./searchIntent";
import { sanitizeStorePreferenceIds } from "./storePreferences";
import { pickDefaultNearbyGrocers } from "./nearbyStoreSelection";
import { parseStoreIntentFromQuery, stripStoreContextMentions } from "./storeIntent";
import * as gtm from "../lib/gtm";

type SubmitAiSearchArgs = {
  rawQuery: string;
  router: NextRouter;
  setSearchQueryError?: (value: string) => void;
  setZipcodeError?: (value: string) => void;
  extraQueryParams?: Record<string, string | string[] | undefined>;
  fallbackZipcode?: string;
  /**
   * Optional replacement for the native window.prompt used when a search
   * still has no location. Resolve with the ZIP the shopper entered, or with
   * undefined when they dismissed the prompt.
   */
  requestManualZipcode?: () => Promise<string | undefined>;
};

type BrowserLocation = {
  latitude: number;
  longitude: number;
};

type SearchIntentApiResponse = Omit<SearchQueryInterpretation, "executedQuery">;
type AuthMeResponse = {
  authenticated?: boolean;
  profile?: {
    zipcode?: string | null;
  } | null;
};

type SearchIntentRequestBody = {
  rawQuery: string;
  browserLocation?: BrowserLocation;
  guestZipcode?: string;
};

type NearbyStoresApiResponse = {
  stores_available?: unknown;
};

/**
 * Outcome of the /api/search/intent round trip. `rejected` is a deliberate 4xx
 * answer that is shown to the shopper; `unavailable` means the API could not
 * answer at all (network, timeout, 5xx, unparsable body) and the client
 * interprets the query itself so the search still runs.
 */
export type IntentFetchOutcome =
  | { kind: "ok"; payload: SearchIntentApiResponse }
  | { kind: "rejected"; status: number; code?: string }
  | { kind: "unavailable"; reason: "network" | "timeout" | "server_error" | "bad_json" };

const LOCATION_PROMPT_MESSAGE =
  "Enter a 5-digit ZIP code to finish this search.";
const GUEST_ZIPCODE_STORAGE_KEY = "guest-search-zipcode";
const EXPLICIT_LOCATION_SIGNAL_REGEX =
  /\b(?:in|around|by|nearby|downtown|uptown|neighborhood|county|state)\b|,/i;
// Mirrors COMPLEX_LOCATION_REGEX in pages/api/search/intent.ts. "near" is
// included (unlike EXPLICIT_LOCATION_SIGNAL_REGEX) because "milk near chicago"
// is anchored by the browser coordinates server-side. It is tested on the
// query with store-only "near <store>" spans removed, because the server
// strips those and uses the known ZIP instead.
const GEO_RELEVANT_SIGNAL_REGEX =
  /\b(?:in|near|around|by|nearby|downtown|uptown|neighborhood|county|state)\b|,/i;
const NEAR_ME_REGEX = /\bnear me\b/i;
const EXPLICIT_ZIP_REGEX = /\b(\d{5})(?:-\d{4})?\b/;
const DANGLING_LOCATION_PREPOSITION_REGEX = /\s+(?:near|in|at)$/i;
// Longer than the server's own intent budget so a slow but successful answer
// is kept; anything slower is treated like an outage.
const INTENT_REQUEST_TIMEOUT_MS = 12000;
// Deliberately low: the results page treats the local interpretation as a
// best-effort reading of the query.
const LOCAL_FALLBACK_CONFIDENCE = 25;

/**
 * Every user-facing sentence shown for an intent API rejection, keyed by the
 * server's error code. Kept in one place so docs can list them.
 */
export const INTENT_ERROR_COPY = {
  origin_forbidden: "This search must be started from getyoudle.com.",
  rate_limit_exceeded: "Too many searches right now. Please try again in a moment.",
  account_deletion_in_progress: "Search is unavailable while this account is being deleted.",
  generic: "Unable to understand this search right now.",
} as const;

export const mapIntentErrorToCopy = (status: number, code?: string): string => {
  if (
    code === "origin_forbidden" ||
    code === "rate_limit_exceeded" ||
    code === "account_deletion_in_progress"
  ) {
    return INTENT_ERROR_COPY[code];
  }
  if (status === 429) {
    return INTENT_ERROR_COPY.rate_limit_exceeded;
  }
  // A 400 carries a human sentence (e.g. the minimum-length message). A
  // snake_case code has no whitespace and is never shown raw to the shopper.
  if (status === 400 && code && /\s/.test(code)) {
    return code;
  }
  return INTENT_ERROR_COPY.generic;
};

const readStoredGuestZipcode = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return normalizeZipcode(window.localStorage.getItem(GUEST_ZIPCODE_STORAGE_KEY));
  } catch {
    return undefined;
  }
};

const storeGuestZipcode = (zipcode?: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedZipcode = normalizeZipcode(zipcode);

  try {
    if (normalizedZipcode) {
      window.localStorage.setItem(GUEST_ZIPCODE_STORAGE_KEY, normalizedZipcode);
    } else {
      window.localStorage.removeItem(GUEST_ZIPCODE_STORAGE_KEY);
    }
  } catch {
    // Ignore localStorage failures.
  }
};

const readAuthenticatedProfileZipcode = async (): Promise<string | undefined> => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const response = await fetch("/api/auth/me", {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      return undefined;
    }

    const payload = (await response.json()) as AuthMeResponse;
    if (!payload.authenticated) {
      return undefined;
    }

    return normalizeZipcode(payload.profile?.zipcode);
  } catch {
    return undefined;
  }
};

const getBrowserLocation = async (): Promise<BrowserLocation | undefined> => {
  if (typeof window === "undefined" || !navigator.geolocation) {
    return undefined;
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => resolve(undefined), 3500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        window.clearTimeout(timeoutId);
        resolve(undefined);
      },
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 120000,
      },
    );
  });
};

/**
 * Browser geolocation costs up to 3.5s and may show a permission prompt, so it
 * is requested only when the server could use the coordinates: "near me" and
 * place-name queries are anchored by them, whereas an explicit ZIP in the query
 * or a known profile/guest ZIP is used as-is by resolveLocation in
 * pages/api/search/intent.ts.
 */
export const shouldRequestBrowserLocation = (
  query: string,
  hasKnownZipcode: boolean,
): boolean => {
  if (EXPLICIT_ZIP_REGEX.test(query)) {
    return false;
  }
  if (NEAR_ME_REGEX.test(query)) {
    return true;
  }
  if (GEO_RELEVANT_SIGNAL_REGEX.test(stripStoreContextMentions(query))) {
    return true;
  }
  return !hasKnownZipcode;
};

const isAbortError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { name?: unknown }).name === "AbortError";

const requestSearchIntent = async (
  body: SearchIntentRequestBody,
): Promise<IntentFetchOutcome> => {
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timeoutId = setTimeout(() => controller?.abort(), INTENT_REQUEST_TIMEOUT_MS);

  try {
    let response: Response;
    try {
      response = await fetch("/api/search/intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
        signal: controller?.signal,
      });
    } catch (error) {
      return { kind: "unavailable", reason: isAbortError(error) ? "timeout" : "network" };
    }

    // A 5xx body is not worth parsing: the server could not interpret the query.
    if (response.status >= 500) {
      return { kind: "unavailable", reason: "server_error" };
    }

    let payload: (SearchIntentApiResponse & { error?: unknown }) | undefined;
    try {
      payload = await response.json();
    } catch (error) {
      if (response.ok) {
        return { kind: "unavailable", reason: isAbortError(error) ? "timeout" : "bad_json" };
      }
      return { kind: "rejected", status: response.status };
    }

    if (!response.ok) {
      return {
        kind: "rejected",
        status: response.status,
        code: typeof payload?.error === "string" ? payload.error : undefined,
      };
    }
    if (!payload || typeof payload !== "object") {
      return { kind: "unavailable", reason: "bad_json" };
    }
    return { kind: "ok", payload };
  } finally {
    // The timer stays armed through body parsing (a stalled body is a timeout
    // too) and is always cleared here so it cannot fire after the request
    // settled or leak as an open handle.
    clearTimeout(timeoutId);
  }
};

/**
 * Secondary lookup: which supported chains are near the shopper. It must never
 * be able to hold up the search. Without its own deadline a stalled
 * /api/stores/available leaves the shopper on the page they submitted from
 * with no navigation and no error, because this await sits between the intent
 * response and router.push.
 */
const NEARBY_STORES_REQUEST_TIMEOUT_MS = 4000;

const requestNearbyStoreIds = async (zipcode: string): Promise<string[]> => {
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timeoutId = setTimeout(() => controller?.abort(), NEARBY_STORES_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`/api/stores/available?zipcode=${encodeURIComponent(zipcode)}`, {
      method: "GET",
      credentials: "include",
      signal: controller?.signal,
    });
    if (!response.ok) {
      return [];
    }

    const payload = (await response.json()) as NearbyStoresApiResponse;
    const rawStores = Array.isArray(payload.stores_available)
      ? payload.stores_available.filter((value): value is string => typeof value === "string")
      : [];

    // The API already returns supported retailer IDs in physical-distance order.
    // Preserve that full list rather than imposing an arbitrary chain count.
    return sanitizeStorePreferenceIds(rawStores);
  } catch {
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Deterministic interpretation used only while /api/search/intent cannot
 * answer. Mirrors the server's buildFallbackSearchIntent in
 * pages/api/search/intent.ts: an explicit ZIP wins, "near me" is flagged and
 * anything else still needs a location from the fallback chain below.
 */
export const buildLocalInterpretation = (trimmedQuery: string): SearchIntentApiResponse => {
  const zipMatch = trimmedQuery.match(EXPLICIT_ZIP_REGEX);
  const resolvedZipcode = zipMatch ? normalizeZipcode(zipMatch[1]) : undefined;
  let productQuery = trimmedQuery;
  if (zipMatch && resolvedZipcode) {
    productQuery =
      trimmedQuery
        .replace(zipMatch[0], " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(DANGLING_LOCATION_PREPOSITION_REGEX, "") || trimmedQuery;
  }

  return {
    originalQuery: trimmedQuery,
    productQuery,
    locationKind: resolvedZipcode ? "zip" : NEAR_ME_REGEX.test(trimmedQuery) ? "near_me" : "none",
    locationNormalization: undefined,
    resolvedZipcode,
    locationSource: resolvedZipcode ? "explicit_zip" : "unresolved",
    priceConstraint: undefined,
    confidence: LOCAL_FALLBACK_CONFIDENCE,
    needsManualLocation: !resolvedZipcode,
  };
};

const buildInterpretation = (
  payload: SearchIntentApiResponse,
  overrides?: {
    resolvedZipcode?: string;
    locationSource?: SearchLocationSource;
    needsManualLocation?: boolean;
  },
): SearchQueryInterpretation => {
  return {
    ...payload,
    executedQuery: payload.productQuery,
    resolvedZipcode: overrides?.resolvedZipcode || payload.resolvedZipcode,
    locationSource: overrides?.locationSource || payload.locationSource,
    needsManualLocation:
      typeof overrides?.needsManualLocation === "boolean"
        ? overrides.needsManualLocation
        : payload.needsManualLocation,
  };
};

export const shouldUseZipcodeFallback = (
  interpretation: Pick<SearchQueryInterpretation, "locationKind">,
): boolean =>
  interpretation.locationKind === "none" || interpretation.locationKind === "near_me";

export const hasExplicitLocationSignal = (rawQuery: string): boolean => {
  const normalized = rawQuery.replace(/\bnear me\b/gi, " ");
  return EXPLICIT_LOCATION_SIGNAL_REGEX.test(normalized);
};

export const canApplyGenericZipcodeFallback = (
  interpretation: Pick<SearchQueryInterpretation, "locationKind">,
  rawQuery: string,
): boolean => shouldUseZipcodeFallback(interpretation) && !hasExplicitLocationSignal(rawQuery);

export async function submitAiSearch({
  rawQuery,
  router,
  setSearchQueryError,
  setZipcodeError,
  extraQueryParams,
  fallbackZipcode,
  requestManualZipcode,
}: SubmitAiSearchArgs) {
  const trimmedQuery = rawQuery.trim();
  if (trimmedQuery.length < 3) {
    setSearchQueryError?.("Your search query must be at least 3 characters long.");
    return;
  }
  const storeIntent = parseStoreIntentFromQuery(trimmedQuery);

  setSearchQueryError?.("");
  setZipcodeError?.("");

  const storedGuestZipcode = readStoredGuestZipcode();
  const hasKnownZipcode = Boolean(normalizeZipcode(fallbackZipcode) || storedGuestZipcode);
  const browserLocation = shouldRequestBrowserLocation(trimmedQuery, hasKnownZipcode)
    ? await getBrowserLocation()
    : undefined;

  const intentOutcome = await requestSearchIntent({
    rawQuery: trimmedQuery,
    browserLocation,
    guestZipcode: storedGuestZipcode,
  });

  if (intentOutcome.kind === "rejected") {
    setSearchQueryError?.(mapIntentErrorToCopy(intentOutcome.status, intentOutcome.code));
    return;
  }

  // Outage path only: interpret the query locally so a stored ZIP still
  // produces a search instead of a dead end. Never used when the API answered.
  const payload =
    intentOutcome.kind === "ok" ? intentOutcome.payload : buildLocalInterpretation(trimmedQuery);

  let interpretation = buildInterpretation(payload);
  let resolvedZipcode = normalizeZipcode(interpretation.resolvedZipcode);
  const allowZipcodeFallback = canApplyGenericZipcodeFallback(interpretation, trimmedQuery);

  if (!resolvedZipcode && allowZipcodeFallback && fallbackZipcode) {
    resolvedZipcode = normalizeZipcode(fallbackZipcode);
    if (resolvedZipcode) {
      interpretation = buildInterpretation(payload, {
        resolvedZipcode,
        locationSource: "follow_up_zip",
        needsManualLocation: false,
      });
      storeGuestZipcode(resolvedZipcode);
    }
  }

  // When the API answered it already applied users.zipcode server-side
  // (resolveLocation's profile branch in pages/api/search/intent.ts), so the
  // /api/auth/me round trip can only add information during an outage.
  if (!resolvedZipcode && allowZipcodeFallback && intentOutcome.kind === "unavailable") {
    const authenticatedProfileZipcode = await readAuthenticatedProfileZipcode();
    if (authenticatedProfileZipcode) {
      resolvedZipcode = authenticatedProfileZipcode;
      interpretation = buildInterpretation(payload, {
        resolvedZipcode,
        locationSource: "profile_zip",
        needsManualLocation: false,
      });
      storeGuestZipcode(resolvedZipcode);
    }
  }

  if (!resolvedZipcode && allowZipcodeFallback && storedGuestZipcode) {
    resolvedZipcode = storedGuestZipcode;
    interpretation = buildInterpretation(payload, {
      resolvedZipcode,
      locationSource: "guest_local_zip",
      needsManualLocation: false,
    });
  }

  if (!resolvedZipcode && interpretation.needsManualLocation && typeof window !== "undefined") {
    const manualInput = requestManualZipcode
      ? await requestManualZipcode()
      : // eslint-disable-next-line no-alert
        window.prompt(LOCATION_PROMPT_MESSAGE);
    const manualZip = normalizeZipcode(manualInput || "");
    if (manualZip) {
      interpretation = buildInterpretation(payload, {
        resolvedZipcode: manualZip,
        locationSource: "manual_prompt",
        needsManualLocation: false,
      });
      resolvedZipcode = manualZip;
      storeGuestZipcode(manualZip);
    }
  }

  if (!resolvedZipcode) {
    setZipcodeError?.("Add a location to run this search.");
    return;
  }

  if (interpretation.locationSource === "guest_local_zip") {
    storeGuestZipcode(resolvedZipcode);
  }

  const params = new URLSearchParams();
  Object.entries(extraQueryParams || {}).forEach(([key, value]) => {
    if (
      key === "searchQuery" ||
      key === "productQuery" ||
      key === "executedQuery" ||
      key === "r" ||
      key === "zipcode" ||
      key === "locationKind" ||
      key === "locationSource" ||
      key === "countryCode" ||
      key === "normalizedPostalCode" ||
      key === "placeName" ||
      key === "adminName1" ||
      key === "adminName2" ||
      key === "priceOp" ||
      key === "priceAmount" ||
      key === "confidence" ||
      key === "needsManualLocation" ||
      key === "allStores" ||
      key === "all_stores" ||
      key === "limit" ||
      key === "store_timeout_s" ||
      key === "overall_timeout_s" ||
      key === "pseudonymous_id"
    ) {
      return;
    }

    if (typeof value === "string" && value.length > 0) {
      params.set(key, value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === "string" && item.length > 0) {
          params.append(key, item);
        }
      });
    }
  });

  appendQueryInterpretationToParams(params, interpretation);
  if (storeIntent.mode === "supported") {
    params.set("stores", storeIntent.storeIds.join(","));
    params.set("storeSelectionMode", "intent");
  } else if (storeIntent.mode === "unsupported_only") {
    params.delete("stores");
    params.set("storeSelectionMode", "none");
  } else {
    const previousSelectionMode = params.get("storeSelectionMode");
    if (previousSelectionMode === "intent" || previousSelectionMode === "nearby") {
      params.delete("stores");
    }
    params.delete("storeSelectionMode");

    // A plain search no longer falls back to Walmart/Aldi/Whole Foods. If the
    // shopper has not explicitly selected stores in the current URL, ask
    // Youdle's current store database which supported chains are actually near
    // the resolved ZIP and search those instead.
    if (!params.has("stores")) {
      const nearbyStoreIds = await requestNearbyStoreIds(resolvedZipcode);
      if (nearbyStoreIds.length > 0) {
        const defaultNearby = pickDefaultNearbyGrocers(nearbyStoreIds);
        params.set("stores", defaultNearby.join(","));
        params.set("storeSelectionMode", "nearby");
      } else {
        // Do not let the grocery scraper silently restore its own Walmart-first
        // defaults when Youdle could not verify a supported nearby retailer.
        params.set("storeSelectionMode", "none");
      }
    }
  }
  params.set("r", String(Date.now()));
  const finalStoreIds = (params.get("stores") || "")
    .split(",")
    .map((storeId) => storeId.trim())
    .filter(Boolean);
  const finalStoreSelectionMode =
    params.get("storeSelectionMode") ||
    (finalStoreIds.length > 0 ? "stored_preference" : "none");
  gtm.searchSubmitted({
    searchTerm: interpretation.executedQuery,
    zipcode: resolvedZipcode,
    locationSource: interpretation.locationSource,
    searchSource:
      router.pathname === "/"
        ? "home"
        : router.pathname === "/search_results"
          ? "search_results"
          : "site_search",
    storeSelectionMode: finalStoreSelectionMode,
    storesRequestedCount: finalStoreIds.length,
  });
  await router.push(`/search_results?${params.toString()}`);
}
